import React, { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { logoutEventKey } from 'features/common/core/constants'
import { clearUserToken, getUserToken } from 'features/common/utils/token'
import PageSpinner from '../../components/PageSpinner/PageSpinner'
import { UserData } from '../../core/entitites'
import getUserData from './api/getUserData'
import signin from './api/signin'
import signup from './api/signup'

interface AuthContextType {
    user: UserData | null
    login: (args: Parameters<typeof signin>[0]) => void
    register: (args: Parameters<typeof signup>[0]) => void
    logout: () => void
    registerError: Error | undefined
    registerLoading: boolean
    loginError: Error | undefined
    loginLoading: boolean
}

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType)
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const qc = useQueryClient()

    const { data, isFetching: userLoading } = useQuery(['user'], getUserData, {
        retry: false,
        enabled: !!getUserToken(),
    })

    const logout = useCallback(() => {
        clearUserToken()
        void qc.setQueryData(['user'], null)
        qc.clear()
    }, [qc])

    useEffect(() => {
        window.addEventListener(logoutEventKey, logout)
        return () => window.removeEventListener(logoutEventKey, logout)
    }, [logout])

    const {
        mutate: register,
        error: registerError,
        isLoading: registerLoading,
    } = useMutation(signup, {
        onSuccess: () => {
            void qc.invalidateQueries(['user'])
        },
    })
    const {
        mutate: login,
        error: loginError,
        isLoading: loginLoading,
    } = useMutation(signin, {
        onSuccess: () => {
            void qc.invalidateQueries(['user'])
        },
    })

    const user = getUserToken() && data ? data : null

    if (userLoading) {
        return <PageSpinner />
    }

    return (
        <AuthContext.Provider
            children={children}
            value={{
                user,
                login,
                logout,
                register,
                registerError: registerError as Error | undefined,
                registerLoading: registerLoading || userLoading,
                loginError: loginError as Error | undefined,
                loginLoading: loginLoading || userLoading,
            }}
        />
    )
}

const useAuth = () => React.useContext(AuthContext)

export { AuthProvider, useAuth }
